import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, Menu, X } from 'lucide-react';
import emailjs from '@emailjs/browser';
import logoAmancio from './amancio.png';
import img1 from './img/img1.jpg';
import img2 from './img/img2.jpg';
import img3 from './img/img3.jpg';
import img4 from './img/img4.jpg';
import img5 from './img/img5.jpg';
import img6 from './img/img6.jpg';
import AdminPortal from './AdminPortal';
import { supabase } from './supabaseClient';

const STATIC_GALLERY = [
  { src: img1, label: 'Outdoor Patio' },
  { src: img2, label: 'Modern Facade' },
  { src: img3, label: 'Custom Staircase' },
  { src: img4, label: 'Interior Lighting' },
  { src: img5, label: 'Living Room Remodel' },
  { src: img6, label: 'Exterior Renovation' },
];

// ============================================================
// EMAILJS CREDENTIALS
// ============================================================
const EMAILJS_SERVICE_ID  = 'service_6nf4kcv';
const EMAILJS_TEMPLATE_ID = 'template_m9q7fxe';
const EMAILJS_PUBLIC_KEY  = 'owmHMVSxIgZDpk1o9';

// ============================================================
// CONTACT FORM WITH EMAILJS
// ============================================================
function ContactForm() {
  const formRef = useRef();
  const [status, setStatus] = useState('idle');

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY)
      .then(() => { setStatus('success'); formRef.current.reset(); })
      .catch(() => setStatus('error'));
  };

  return (
    <form ref={formRef} className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <label className="block text-slate-700 font-semibold mb-2 text-sm">Full Name *</label>
        <input name="from_name" type="text" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition text-slate-800" placeholder="John Smith" required />
      </div>
      <div>
        <label className="block text-slate-700 font-semibold mb-2 text-sm">Email *</label>
        <input name="from_email" type="email" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition text-slate-800" placeholder="john@example.com" required />
      </div>
      <div>
        <label className="block text-slate-700 font-semibold mb-2 text-sm">Phone *</label>
        <input name="from_phone" type="tel" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition text-slate-800" placeholder="(702) 000-0000" required />
      </div>
      <div>
        <label className="block text-slate-700 font-semibold mb-2 text-sm">Service of Interest</label>
        <select name="service_interest" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition text-slate-800 bg-white">
          <option value="">Select a service...</option>
          <option>Kitchen Remodeling</option>
          <option>Bathroom Remodeling</option>
          <option>Flooring & Finishes</option>
          <option>General Construction</option>
          <option>Landscaping</option>
          <option>Full Renovation</option>
          <option>Other</option>
        </select>
      </div>
      <div>
        <label className="block text-slate-700 font-semibold mb-2 text-sm">Message *</label>
        <textarea name="message" rows="4" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition resize-none text-slate-800" placeholder="Tell us about your project..." required></textarea>
      </div>
      {status === 'success' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 font-semibold text-sm">
          ✅ Message sent! We'll get back to you within 24 hours.
        </div>
      )}
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 font-semibold text-sm">
          ⚠️ Something went wrong. Please call us at (702) 576-1958.
        </div>
      )}
      <button type="submit" disabled={status === 'sending'}
        className={`w-full text-white py-4 rounded-xl font-bold text-lg transition shadow-lg ${status === 'sending' ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 transform hover:scale-105'}`}>
        {status === 'sending' ? '⏳ Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

// ============================================================
// MAIN APP
// ============================================================
function App() {
  const [view, setView] = useState('web'); // web | portal
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState(STATIC_GALLERY);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('gallery')
          .list('', { sortBy: { column: 'created_at', order: 'desc' } });
        if (error || !data) return;
        const files = data.filter(f => f.name !== '.emptyFolderPlaceholder');
        if (files.length === 0) return; // keep static images if bucket is empty
        const photos = files.slice(0, 10).map(f => {
          const { data: urlData } = supabase.storage.from('gallery').getPublicUrl(f.name);
          return { src: urlData.publicUrl, label: 'Project Photo' };
        });
        setGalleryPhotos(photos);
      } catch (e) {
        // silently fall back to static images
      }
    };
    fetchGallery();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // Show admin portal
  if (view === 'portal') return <AdminPortal onBack={() => setView('web')} />;

  return (
    <div className="min-h-screen bg-white">

      {/* ══════════ HEADER ══════════ */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img src={logoAmancio} alt="Amancio Remodeling" className="h-14 w-auto" />
            </div>
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-1">
              {[['inicio', 'Home'], ['servicios', 'Services'], ['sobre-nosotros', 'About Us'], ['galeria', 'Gallery'], ['contacto', 'Contact']].map(([id, label]) => (
                <button key={id} onClick={() => scrollToSection(id)}
                  className={`font-semibold text-sm px-4 py-2 rounded-lg transition ${scrolled ? 'text-slate-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'}`}>
                  {label}
                </button>
              ))}
              {/* Admin — subtle link */}
              <button onClick={() => setView('portal')}
                className={`ml-3 text-sm px-3 py-2 rounded-lg border transition ${scrolled ? 'text-slate-400 border-slate-200 hover:border-slate-400 hover:text-slate-600' : 'text-white/50 border-white/15 hover:text-white/80 hover:border-white/30'}`}>
                Admin
              </button>
            </nav>
            {/* Mobile toggle */}
            <button className={`md:hidden ${scrolled ? 'text-slate-800' : 'text-white'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className={`md:hidden py-4 border-t ${scrolled ? 'border-slate-200' : 'border-white/20'}`}>
              <div className="flex flex-col space-y-2">
                {[['inicio', 'Home'], ['servicios', 'Services'], ['sobre-nosotros', 'About Us'], ['galeria', 'Gallery'], ['contacto', 'Contact']].map(([id, label]) => (
                  <button key={id} onClick={() => scrollToSection(id)}
                    className={`font-medium text-left px-2 py-2 ${scrolled ? 'text-slate-700' : 'text-white'}`}>{label}</button>
                ))}
                <button onClick={() => setView('portal')} className="text-slate-400 hover:text-slate-600 text-left px-2 py-1 text-sm">Admin →</button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ══════════ HERO ══════════ */}
      <section id="inicio" className="pt-24 pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block bg-emerald-500/20 border border-emerald-400/30 rounded-full px-6 py-2 mb-6">
            <span className="text-emerald-300 text-sm font-semibold tracking-widest uppercase">Las Vegas, Nevada</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 drop-shadow-lg leading-tight">
            We Transform<br /><span className="text-emerald-400">Your Home</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-slate-200 leading-relaxed">
            Professional remodeling services with over 20 years of experience in Las Vegas. Guaranteed quality and personalized attention on every project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('contacto')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition transform hover:scale-105 shadow-xl">
              Request Your Free Quote
            </button>
            <button onClick={() => scrollToSection('servicios')}
              className="bg-white/10 border border-white/30 hover:bg-white/20 text-white px-8 py-4 rounded-xl text-lg font-semibold transition backdrop-blur-sm">
              View Our Services
            </button>
          </div>
        </div>
      </section>

      {/* ══════════ SERVICES ══════════ */}
      <section id="servicios" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">What We Do</span>
            <h2 className="text-4xl font-bold text-slate-800 mt-3 mb-4">Our Services</h2>
            <p className="text-xl text-slate-600">Complete remodeling solutions for your home or business</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🏠', title: 'Kitchen Remodeling', desc: 'We design and build your dream kitchen using high-quality materials and expert craftsmanship.' },
              { icon: '🛁', title: 'Bathroom Remodeling', desc: 'We transform your bathroom into an elegant and functional space with modern design.' },
              { icon: '🪵', title: 'Flooring & Finishes', desc: 'Hardwood, vinyl, ceramic, marble — we install any material with precision and care.' },
              { icon: '🏗️', title: 'General Construction', desc: 'Room additions, garages, decks and structural improvements for your property.' },
              { icon: '🌿', title: 'Landscaping', desc: 'Professional landscaping, irrigation systems and outdoor design to enhance your curb appeal.' },
              { icon: '🔨', title: 'Full Renovations', desc: 'Complete home makeovers — from planning to final finishes, we handle it all.' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition border border-slate-100 hover:border-emerald-200">
                <div className="text-4xl mb-4">{s.icon}</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ ABOUT US ══════════ */}
      <section id="sobre-nosotros" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Who We Are</span>
              <h2 className="text-4xl font-bold text-slate-800 mt-3 mb-6">20 Years of Excellence in Las Vegas</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Amancio Remodeling is a family-owned business with over 20 years of experience in Las Vegas. We specialize in transforming homes with superior quality, honest pricing, and exceptional customer service.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Every project is treated as if it were our own home — with care, precision, and pride in every detail.
              </p>
              <button onClick={() => scrollToSection('contacto')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition">
                Get a Free Consultation
              </button>
            </div>
            <div className="grid grid-cols-3 gap-6 text-center">
              {[['500+', 'Projects Completed'], ['20+', 'Years of Experience'], ['100%', 'Client Satisfaction']].map(([num, label]) => (
                <div key={label} className="bg-slate-50 rounded-2xl p-6">
                  <p className="text-4xl font-bold text-emerald-600 mb-2">{num}</p>
                  <p className="text-slate-600 text-sm font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ GALLERY ══════════ */}
      <section id="galeria" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Our Work</span>
            <h2 className="text-4xl font-bold text-slate-800 mt-3">Recent Projects</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryPhotos.map((photo, i) => (
              <div key={i} className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300">
                <img src={photo.src} alt={photo.label} className="w-full h-56 object-cover transform group-hover:scale-105 transition duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white font-semibold text-base p-4">{photo.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CONTACT US ══════════ */}
      <section id="contacto" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-emerald-600 text-sm font-bold uppercase tracking-widest">Get In Touch</span>
            <h2 className="text-4xl font-bold text-slate-800 mt-3 mb-4">Contact Us</h2>
            <p className="text-xl text-slate-600">Ready to start your project? We'd love to hear from you.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-emerald-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">Phone</h3>
                  <a href="tel:+17025761958" className="text-emerald-600 hover:text-emerald-700 font-semibold transition">(702) 576-1958</a>
                  <p className="text-slate-500 text-sm mt-1">Mon–Sat, 7am–7pm</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-emerald-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">Email</h3>
                  <a href="mailto:amancioremodelingllc@gmail.com" className="text-emerald-600 hover:text-emerald-700 font-semibold transition">amancioremodelingllc@gmail.com</a>
                  <p className="text-slate-500 text-sm mt-1">We reply within 24 hours</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-emerald-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">Service Area</h3>
                  <p className="text-slate-600">Las Vegas, Henderson, North Las Vegas</p>
                  <p className="text-slate-500 text-sm mt-1">And surrounding Clark County areas</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="text-emerald-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">Business Hours</h3>
                  <p className="text-slate-600">Monday – Saturday: 7:00 AM – 7:00 PM</p>
                  <p className="text-slate-500 text-sm mt-1">Sunday: Closed</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8 shadow-sm border border-slate-100">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Send Us a Message</h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            <div>
              <img src={logoAmancio} alt="Amancio Remodeling" className="h-14 mb-4" />
              <p className="text-slate-300 text-sm leading-relaxed">Transforming homes in Las Vegas with quality and professionalism for over 20 years.</p>
            </div>
            <div>
              <h4 className="text-emerald-400 font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                {['Kitchen Remodeling', 'Bathroom Remodeling', 'Flooring & Finishes', 'General Construction', 'Landscaping'].map(s => (
                  <li key={s}><button onClick={() => scrollToSection('servicios')} className="text-slate-300 hover:text-emerald-400 transition">{s}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-emerald-400 font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                {[['inicio', 'Home'], ['sobre-nosotros', 'About Us'], ['galeria', 'Gallery'], ['contacto', 'Contact']].map(([id, label]) => (
                  <li key={id}><button onClick={() => scrollToSection(id)} className="text-slate-300 hover:text-emerald-400 transition">{label}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-emerald-400 font-bold text-lg mb-4">Follow Us</h4>
              <p className="text-slate-300 text-sm mb-4">Connect with us on social media</p>
              <div className="flex space-x-4 text-2xl">
                <a href="https://www.instagram.com/amancioremodelingllc" rel="noopener noreferrer" target="_blank" className="text-slate-300 hover:text-emerald-400 transition">📷</a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center">
            <p className="text-slate-400 text-sm">© 2026 Amancio Remodeling. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
